import { useEffect, useState } from 'react';
import { IconReportMoney, IconAlertCircle, IconPrinter } from '@tabler/icons-react';
import Modal from './ui/Modal';

/**
 * DayReportModal — Phase 2.9.
 *
 * Renders the X-report (mid-day read) or Z-report (closing report)
 * for a given day session. Same payload either way; only the kind
 * label changes.
 *
 * Fetched on open. Re-fetch on demand via the Refresh button.
 */

interface ReportShape {
  session: {
    id: string;
    openedAt: string;
    closedAt: string | null;
    openingFloatCents: number;
    closingFloatCents: number | null;
    countedCashCents: number | null;
    expectedCashCents: number | null;
    varianceCents: number | null;
    openedBy: { name: string } | null;
    closedBy: { name: string } | null;
    closeNotes: string | null;
  };
  totals: {
    grossSalesCents: number;
    discountCents: number;
    refundedCents: number;
    cancelledCents: number;
    voidedCents: number;
    netSalesCents: number;
    tipCents: number;
    vatCents: number;
    counts: Record<string, number>;
  };
  paymentsByMethod: Array<{
    method: string;
    count: number;
    amountCents: number;
    refundedAmountCents: number;
    netCents: number;
  }>;
  drawer: {
    paidInCents: number;
    paidOutCents: number;
    paidInCount: number;
    paidOutCount: number;
    movements: Array<{
      id: string;
      direction: string;
      amountCents: number;
      reason: string;
      at: string;
      actor: { name: string } | null;
    }>;
  };
  cash: {
    openingFloatCents: number;
    cashSalesNetCents: number;
    paidInCents: number;
    paidOutCents: number;
    expectedCashCents: number;
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** When true, fetch /x-report (current open session). */
  kind: 'X' | 'Z';
  /** Required for Z-report — which closed session to fetch. */
  sessionId?: string;
}

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

function methodLabel(m: string) {
  return m === 'CARD_YOCO' ? 'Card' :
         m === 'CASH' ? 'Cash' :
         m === 'EFT' ? 'EFT' :
         m === 'ACCOUNT' ? 'Account' :
         m === 'VOUCHER' ? 'Voucher' :
         m === 'GIFT_CARD' ? 'Gift card' :
         'Other';
}

export default function DayReportModal({ open, onClose, kind, sessionId }: Props) {
  const [report, setReport] = useState<ReportShape | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function fetchReport() {
    setLoading(true);
    setError(null);
    try {
      const url = kind === 'X'
        ? '/api/day-sessions/x-report'
        : `/api/day-sessions/${sessionId}/z-report`;
      const r = await fetch(url, { credentials: 'include' });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error ?? `HTTP ${r.status}`);
      }
      const data = await r.json();
      setReport(data.report);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) {
      setReport(null);
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind, sessionId]);

  return (
    <Modal open={open} onClose={onClose} size="xl" title={kind === 'X' ? 'X-Report — current day read' : 'Z-Report — day close'}>
      {loading && (
        <p className="text-13 font-semibold text-gray-700 py-8 text-center">Loading…</p>
      )}

      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      {report && (
        <div className="space-y-5">
          {/* Header strip */}
          <div className="rounded-lg bg-light border border-border p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-13 font-bold uppercase tracking-wider text-gray-700 inline-flex items-center gap-2">
                <IconReportMoney size={16} stroke={2.25} className="text-primary" />
                {kind === 'X' ? 'Current open session' : 'Closed session'}
              </p>
              <button
                type="button"
                onClick={() => fetchReport()}
                className="text-12 font-bold text-primary hover:underline"
              >
                Refresh
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-13">
              <div>
                <p className="font-semibold text-gray-700">Opened</p>
                <p className="font-bold text-heading">{new Date(report.session.openedAt).toLocaleString('en-ZA')}</p>
                <p className="font-medium text-gray-700 mt-0.5">by {report.session.openedBy?.name ?? '—'}</p>
              </div>
              <div>
                <p className="font-semibold text-gray-700">Closed</p>
                <p className="font-bold text-heading">{report.session.closedAt ? new Date(report.session.closedAt).toLocaleString('en-ZA') : '— still open —'}</p>
                <p className="font-medium text-gray-700 mt-0.5">{report.session.closedBy?.name ? `by ${report.session.closedBy.name}` : ''}</p>
              </div>
            </div>
          </div>

          {/* Sales totals */}
          <section>
            <p className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">Sales</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-surface border border-border p-3">
                <p className="text-12 font-semibold text-gray-700">Gross sales</p>
                <p className="text-20 font-bold text-heading tabular-nums">{fmtR(report.totals.grossSalesCents)}</p>
              </div>
              <div className="rounded-lg bg-primary-soft border border-primary/30 p-3">
                <p className="text-12 font-bold text-primary uppercase tracking-wider">Net sales</p>
                <p className="text-22 font-bold text-primary tabular-nums">{fmtR(report.totals.netSalesCents)}</p>
              </div>
              <div className="rounded-lg bg-surface border border-border p-3">
                <p className="text-12 font-semibold text-gray-700">VAT (incl.)</p>
                <p className="text-16 font-bold text-heading tabular-nums">{fmtR(report.totals.vatCents)}</p>
              </div>
              <div className="rounded-lg bg-surface border border-border p-3">
                <p className="text-12 font-semibold text-gray-700">Tips</p>
                <p className="text-16 font-bold text-heading tabular-nums">{fmtR(report.totals.tipCents)}</p>
              </div>
              <div className="rounded-lg bg-surface border border-border p-3">
                <p className="text-12 font-semibold text-gray-700">Discounts given</p>
                <p className="text-16 font-bold text-heading tabular-nums">{fmtR(report.totals.discountCents)}</p>
              </div>
              <div className="rounded-lg bg-surface border border-border p-3">
                <p className="text-12 font-semibold text-gray-700">Refunded</p>
                <p className="text-16 font-bold text-heading tabular-nums">{fmtR(report.totals.refundedCents)}</p>
              </div>
            </div>
            {(report.totals.cancelledCents > 0 || report.totals.voidedCents > 0) && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                {report.totals.cancelledCents > 0 && (
                  <div className="rounded-lg bg-warning-soft border border-warning/30 p-3">
                    <p className="text-12 font-semibold text-warning uppercase tracking-wider">Cancelled</p>
                    <p className="text-14 font-bold text-warning tabular-nums">{fmtR(report.totals.cancelledCents)}</p>
                  </div>
                )}
                {report.totals.voidedCents > 0 && (
                  <div className="rounded-lg bg-danger-soft border border-danger/30 p-3">
                    <p className="text-12 font-semibold text-danger uppercase tracking-wider">Voided</p>
                    <p className="text-14 font-bold text-danger tabular-nums">{fmtR(report.totals.voidedCents)}</p>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Payments by method */}
          <section>
            <p className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">By payment method</p>
            {report.paymentsByMethod.length === 0 ? (
              <p className="text-13 italic text-gray-700">No payments yet.</p>
            ) : (
              <ul className="space-y-2">
                {report.paymentsByMethod.map((p) => (
                  <li key={p.method} className="flex items-center justify-between p-3 rounded-md bg-surface border border-border">
                    <div>
                      <p className="text-14 font-bold text-heading">{methodLabel(p.method)}</p>
                      <p className="text-12 font-medium text-gray-700">{p.count} {p.count === 1 ? 'payment' : 'payments'}{p.refundedAmountCents > 0 ? ` · refunds ${fmtR(p.refundedAmountCents)}` : ''}</p>
                    </div>
                    <p className="text-16 font-bold text-heading tabular-nums">{fmtR(p.netCents)}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Cash drawer */}
          <section>
            <p className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">Cash drawer</p>
            <div className="rounded-lg bg-light border border-border p-4 space-y-2">
              <div className="flex justify-between text-14"><span className="font-semibold text-gray-700">Opening float</span><span className="font-bold text-heading tabular-nums">{fmtR(report.cash.openingFloatCents)}</span></div>
              <div className="flex justify-between text-14"><span className="font-semibold text-gray-700">+ Cash sales (net)</span><span className="font-bold text-heading tabular-nums">{fmtR(report.cash.cashSalesNetCents)}</span></div>
              <div className="flex justify-between text-14"><span className="font-semibold text-gray-700">+ Paid in</span><span className="font-bold text-heading tabular-nums">{fmtR(report.cash.paidInCents)}</span></div>
              <div className="flex justify-between text-14"><span className="font-semibold text-gray-700">− Paid out</span><span className="font-bold text-heading tabular-nums">−{fmtR(report.cash.paidOutCents)}</span></div>
              <div className="border-t border-border pt-2 flex justify-between text-15"><span className="font-bold text-heading">Expected in drawer</span><span className="font-bold text-primary tabular-nums">{fmtR(report.cash.expectedCashCents)}</span></div>
              {report.session.countedCashCents !== null && (
                <>
                  <div className="flex justify-between text-14"><span className="font-semibold text-gray-700">Counted</span><span className="font-bold text-heading tabular-nums">{fmtR(report.session.countedCashCents)}</span></div>
                  {report.session.varianceCents !== null && (
                    <div className={`flex justify-between text-15 ${report.session.varianceCents === 0 ? 'text-success' : 'text-warning'}`}>
                      <span className="font-bold">Variance</span>
                      <span className="font-bold tabular-nums">{report.session.varianceCents >= 0 ? '+' : ''}{fmtR(report.session.varianceCents)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            {(report.drawer.paidInCount + report.drawer.paidOutCount) > 0 && (
              <details className="mt-3">
                <summary className="text-12 font-bold text-primary cursor-pointer hover:underline">
                  {report.drawer.paidInCount + report.drawer.paidOutCount} drawer movements
                </summary>
                <ul className="mt-2 space-y-1.5">
                  {report.drawer.movements.map((m) => (
                    <li key={m.id} className="flex items-center justify-between text-12 px-2 py-1.5 rounded-md bg-surface border border-border">
                      <div>
                        <p className="font-bold text-heading">{m.direction === 'PAID_IN' ? 'Paid in' : 'Paid out'} · {m.reason}</p>
                        <p className="font-medium text-gray-700">{new Date(m.at).toLocaleTimeString('en-ZA')} · {m.actor?.name ?? '—'}</p>
                      </div>
                      <p className={`font-bold tabular-nums ${m.direction === 'PAID_IN' ? 'text-success' : 'text-danger'}`}>
                        {m.direction === 'PAID_IN' ? '+' : '−'}{fmtR(m.amountCents)}
                      </p>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </section>

          {/* Order counts */}
          {Object.keys(report.totals.counts).length > 0 && (
            <section>
              <p className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">Order counts</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(report.totals.counts).map(([status, n]) => (
                  <span key={status} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-surface border border-border text-13 font-semibold text-heading">
                    <span className="text-gray-700">{status.toLowerCase().replace(/_/g, ' ')}</span>
                    <span className="font-bold tabular-nums text-primary">{n}</span>
                  </span>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <div className="mt-6 flex items-stretch gap-2">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 h-12 rounded-md bg-light border border-border text-14 font-bold text-heading hover:bg-gray-100 transition-colors"
        >
          Close
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          disabled={!report}
          className="flex-1 h-12 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-md"
        >
          <IconPrinter size={16} stroke={2.25} /> Print
        </button>
      </div>
    </Modal>
  );
}
